import React, { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import { Button, FormControl, InputLabel, MenuItem, Select as MuiSelect, Stack, TextField } from "@mui/material";
import { Drawer } from "components/Characteristics/Create-update/Drawer";
import { useFormik } from "formik";
import { useActionHook } from "hooks";
import { IApiServiceError, useClientActions } from "store";
import { ClientType, CreateClientPayload, IClient } from "store/types/user-management";
import apiErrorsToFormik from "utils/api-errors-to-formik";

import { clientTypeOptions, currenciesArray } from "../../../../constants";

interface IClientDrawerProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (clientId: number) => void;
  onNavigateToClient?: (clientId: number) => void;
  /** When provided the drawer is in edit mode (PATCH); otherwise it creates a new client. */
  client?: IClient | null;
}

interface IClientForm {
  name: string;
  type: ClientType | "";
  rm: string;
  domicile: string;
  currency: string;
}

const validationSchema = yup.object({
  name: yup.string().trim().required("Client name is required.")
});

/**
 * Slide-in drawer for creating or editing a client. Form state is managed with Formik + yup
 * (mirrors the user create/edit drawer). Create sends the full payload; edit PATCHes name + type
 * only (rm/domicile/currency are characteristic values, edited on the client page).
 */
export const ClientDrawer: React.FC<IClientDrawerProps> = ({ open, onClose, onSuccess, onNavigateToClient, client }) => {
  const { createClientAction, updateClientAction } = useClientActions();
  const isEdit = Boolean(client);

  const initialValues = useMemo<IClientForm>(
    () => ({
      name: client?.name ?? "",
      type: client?.type ?? "",
      rm: "",
      domicile: "",
      currency: ""
    }),
    [client]
  );

  const finishSuccess = (savedId: number, canNavigate: boolean) => {
    toast.success(isEdit ? "Client updated" : "Client created successfully");
    onClose();
    onSuccess(savedId);
    if (canNavigate) onNavigateToClient?.(savedId);
  };

  const onReject = (error: IApiServiceError) => {
    // Map per-field validation errors (name/type/…) onto the form; toast the rest.
    const fieldErrors = apiErrorsToFormik(initialValues, error?.errors ?? {});
    if (Object.keys(fieldErrors).length) {
      setErrors(fieldErrors);
    } else {
      toast.error(error?.message || "Something went wrong. Please try again.");
    }
  };

  const { action: createClient, loading: creating } = useActionHook(createClientAction, {
    onSuccess: (created) => finishSuccess(created.id, true),
    onReject
  });

  const { action: updateClient, loading: updating } = useActionHook(updateClientAction, {
    onSuccess: (updated) => finishSuccess(updated.id, false),
    onReject
  });

  const isSaving = creating || updating;

  const { values, errors, touched, isValid, handleChange, handleBlur, submitForm, resetForm, setErrors } =
    useFormik<IClientForm>({
      initialValues,
      enableReinitialize: true,
      validationSchema,
      validateOnMount: true,
      onSubmit: (form) => {
        if (isEdit && client) {
          updateClient(client.id, { name: form.name.trim(), type: form.type || null });
          return;
        }

        const rm = form.rm.trim();
        const domicile = form.domicile.trim();
        const currency = form.currency.trim();

        const payload: CreateClientPayload = {
          name: form.name.trim(),
          type: form.type || null,
          ...(rm ? { rm } : {}),
          ...(domicile ? { domicile } : {}),
          ...(currency ? { primary_currency: currency } : {})
        };

        createClient(payload);
      }
    });

  // The drawer keeps its children mounted between open/close, so reset on each open.
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open, resetForm]);

  return (
    <Drawer open={open} onClose={isSaving ? undefined : () => onClose()} title={isEdit ? "Edit client" : "Add client"}>
      <Stack sx={{ p: "30px", gap: "16px", flex: 1, overflow: "auto", minHeight: 0 }}>
        <TextField
          name="name"
          label="Client name"
          fullWidth
          value={values.name}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isSaving}
          error={touched.name && Boolean(errors.name)}
          helperText={touched.name && errors.name}
          inputProps={{ "aria-label": "client name" }}
          autoFocus
        />

        <FormControl fullWidth disabled={isSaving} error={touched.type && Boolean(errors.type)}>
          <InputLabel id="client-type-label">Type</InputLabel>
          <MuiSelect
            name="type"
            labelId="client-type-label"
            label="Type"
            value={values.type}
            onChange={handleChange}
            onBlur={handleBlur}
            inputProps={{ "aria-label": "client type" }}
            displayEmpty
          >
            <MenuItem value="" disabled>
              Select type from list
            </MenuItem>
            {clientTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </MuiSelect>
        </FormControl>

        {/* rm / domicile / currency are initial characteristic values — creation only. */}
        {!isEdit && (
          <>
            <TextField
              name="rm"
              label="RM"
              fullWidth
              value={values.rm}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSaving}
              placeholder="Relationship manager"
              inputProps={{ "aria-label": "relationship manager" }}
            />

            <TextField
              name="domicile"
              label="Domicile"
              fullWidth
              value={values.domicile}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={isSaving}
              inputProps={{ "aria-label": "domicile" }}
            />

            <FormControl fullWidth disabled={isSaving}>
              <InputLabel id="currency-label">Currency</InputLabel>
              <MuiSelect
                name="currency"
                labelId="currency-label"
                label="Currency"
                value={values.currency}
                onChange={handleChange}
                onBlur={handleBlur}
                inputProps={{ "aria-label": "currency" }}
                displayEmpty
                renderValue={(value) => (value as string) || "Select currency"}
              >
                <MenuItem value="" disabled>
                  Select currency
                </MenuItem>
                {currenciesArray.map((code) => (
                  <MenuItem key={code} value={code}>
                    {code}
                  </MenuItem>
                ))}
              </MuiSelect>
            </FormControl>
          </>
        )}
      </Stack>

      <Stack
        direction="row"
        justifyContent="flex-end"
        alignItems="center"
        sx={{ p: "24px", gap: "10px", borderTop: "1px solid #dae0e2" }}
      >
        <Button variant="outlined" disabled={isSaving} onClick={onClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isValid || isSaving}
          onClick={submitForm}
          sx={{ textTransform: "none", bgcolor: "#071F35", "&:hover": { bgcolor: "#0a3142" } }}
        >
          {isEdit ? "Save changes" : "Add client"}
        </Button>
      </Stack>
    </Drawer>
  );
};

export default ClientDrawer;
